import {
    CardItem,
    CollectionEvent,
    createCardClickedEvent,
    isPublicationCardItem,
    toLinkWithHint,
    useOnScreen,
} from '@news-mono/web-common'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import React, { useRef } from 'react'
import {
    StyledCTAButton,
    StyledCardImage,
    StyledCardImageContainer,
    StyledContentCard,
    StyledLinkWrapper,
    StyledText,
    StyledTextContainer,
    StyledTimeToRead,
} from './ContentCard.styled'

export type CardType = 'large' | 'medium' | 'small'

interface CTAProps {
    title: string
    link?: string
}

export interface ContentCardProps {
    item: MaybeLoaded<CardItem>
    cardNumber: number
    cardType: CardType
    onEvent: (event: CollectionEvent) => void
    gridAreaName?: string
    displayRightSeparator?: boolean
    cta?: CTAProps
    shouldHideTeaser?: boolean
}

const getImageWidth = (cardType: CardType) => {
    switch (cardType) {
        case 'small':
            return '350px'
        case 'medium':
            return '740px'
        case 'large':
            return '740px'
    }
}

export const ContentCard: React.FC<ContentCardProps> = ({
    item,
    cardNumber,
    cardType,
    onEvent,
    gridAreaName,
    displayRightSeparator,
    cta,
    shouldHideTeaser,
}) => {
    function linkClicked(
        e: React.MouseEvent<HTMLElement>,
        item: MaybeLoaded<CardItem>,
        cardNumber: number,
    ) {
        if (!item.loaded) {
            e.preventDefault()
            return
        }
        onEvent(
            createCardClickedEvent(
                onEvent,
                item.result,
                'InternalPortrait',
                'Portrait',
                cardNumber,
                'InlineRelatedCollection',
            ),
        )
    }

    const cardRef = useRef<HTMLDivElement>(null)
    const isVisible = useOnScreen(cardRef)

    if (item.loaded && isPublicationCardItem(item.result)) {
        const timeToRead = item?.result?.readTimeMinutes
            ? `${Math.ceil(item.result.readTimeMinutes)} min read`
            : ''

        return (
            <StyledLinkWrapper
                to={toLinkWithHint(item)}
                onClick={(event) => {
                    linkClicked(event, item, cardNumber)
                }}
                gridAreaName={gridAreaName}
            >
                <StyledContentCard
                    ref={cardRef}
                    cardType={cardType}
                    displayRightSeparator={displayRightSeparator}
                >
                    <StyledTextContainer cardType={cardType}>
                        <StyledText
                            item={{
                                loaded: true,
                                result: item.result,
                            }}
                            fontScale={1}
                            onEvent={onEvent}
                            teaserMode={
                                cardType === 'small' || shouldHideTeaser
                                    ? 'hidden'
                                    : 'visible'
                            }
                            kickerMode={'hidden'}
                        />
                        {timeToRead && !cta && (
                            <StyledTimeToRead>{timeToRead}</StyledTimeToRead>
                        )}

                        {cta && <StyledCTAButton>{cta.title}</StyledCTAButton>}
                    </StyledTextContainer>
                    <StyledCardImageContainer cardType={cardType}>
                        <StyledCardImage
                            item={{
                                loaded: true,
                                result: item.result,
                            }}
                            imageWidths={{
                                mobile: getImageWidth(cardType),
                                tablet: getImageWidth(cardType),
                                desktop: getImageWidth(cardType),
                                fallbackWidth: 740,
                            }}
                            hasBackground={true}
                            onEvent={onEvent}
                            mediaMode={'default'}
                            fixedRatio={'16:9'}
                            disableImageLazyLoad={isVisible}
                        />
                    </StyledCardImageContainer>
                </StyledContentCard>
            </StyledLinkWrapper>
        )
    } else {
        return null
    }
}
